/**
 * Invitation Page - handles invitation links
 * Redirects authenticated users to accept invitation
 * Redirects unauthenticated users to registration with invitation token
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import * as invitationsApi from '../services/invitationsApi';

export const InvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitationInfo, setInvitationInfo] = useState<{
    valid: boolean;
    company_name?: string;
    message?: string;
  } | null>(null);

  useEffect(() => {
    if (token) {
      checkInvitation();
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [token]);

  const checkInvitation = async () => {
    if (!token) return;

    try {
      const info = await invitationsApi.checkInvitationToken(token);
      setInvitationInfo(info);

      if (!info.valid) {
        setError(info.message || 'Invalid or expired invitation');
        setLoading(false);
        return;
      }

      // If user is authenticated, accept invitation directly
      if (isAuthenticated) {
        await acceptInvitation();
      } else {
        // Redirect to registration with token
        navigate(`/register?token=${token}`);
      }
    } catch (err) {
      console.error('Error checking invitation:', err);
      setError('Failed to check invitation');
      setLoading(false);
    }
  };

  const acceptInvitation = async () => {
    if (!token) return;

    try {
      await invitationsApi.acceptInvitation(token);
      // Redirect to omnichannel (main page) after accepting
      navigate('/omnichannel');
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError('Failed to accept invitation');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white shadow-md rounded-lg p-8">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="mt-4 text-center text-2xl font-bold text-gray-900">
              Invalid Invitation
            </h2>
            <p className="mt-2 text-center text-gray-600">{error}</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/')}
                className="btn btn-primary w-full"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
